import React from 'react'
import { Link } from 'react-router-dom'
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'

const DECISION_OPTIONS = [
  { key: 'satisfactory', label: 'Close — Satisfactory', desc: 'No further action', color: '#16a34a' },
  { key: 'enhanced_monitoring', label: 'Enhanced Monitoring', desc: 'Periodic review', color: '#ca8a04' },
  { key: 'escalated_smr', label: 'Escalate → SMR', desc: 'File SMR', color: '#dc2626' },
]

function decisionLabel(key) {
  if (key === 'satisfactory') return 'Close — Satisfactory'
  if (key === 'enhanced_monitoring') return 'Enhanced Monitoring'
  if (key === 'escalated_smr') return 'Escalate to SMR'
  return key || 'N/A'
}

/**
 * Decision capture at the bottom of the ECDD form (inline, not fixed).
 */
export default function EcddDecisionPanel({
  isClosed,
  isPendingApproval,
  isApproved,
  isApprovedSmr,
  isApprovedMonitoring,
  formGatesReady,
  formGatesDone,
  formGatesTotal,
  selectedDecision,
  setSelectedDecision,
  decisionRationale,
  setDecisionRationale,
  submittingDecision,
  submitDecisionError,
  checkingTriggers,
  matchedTriggers,
  requiresApproval,
  checkingApprovalPermission,
  canApproveEcdd,
  approvingEcdd,
  returningEcdd,
  approvalError,
  showReturnModal,
  setShowReturnModal,
  returnReason,
  setReturnReason,
  closingEcdd,
  onSubmitForApproval,
  onApproveEcdd,
  onReturnEcdd,
  onCloseEcdd,
  onOpenPreview,
  onScrollToIncomplete,
}) {
  const outcomeBadge = isClosed
    ? 'Closed'
    : isPendingApproval
      ? 'Pending'
      : isApproved
        ? 'Approved'
        : formGatesReady
          ? 'Ready'
          : 'Incomplete'

  return (
    <Card
      className="border border-slate-200 shadow-sm overflow-hidden border-t-4 border-t-psg-teal bg-white"
      aria-label="ECDD decision and submit"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheckIcon
              className={`w-5 h-5 shrink-0 ${formGatesReady || isClosed ? 'text-green-600' : 'text-slate-400'}`}
            />
            <div>
              <div className="text-sm font-bold text-psg-teal leading-tight">Decision &amp; outcome</div>
              {!formGatesReady && !isClosed && !isPendingApproval && !isApproved && (
                <p className="text-[11px] text-slate-500 m-0">
                  Complete required sections in the left menu before recording a decision.
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
              style={{
                background: isClosed || formGatesReady ? '#f0fdf4' : '#fef2f2',
                color: isClosed || formGatesReady ? '#15803d' : '#dc2626',
              }}
            >
              {outcomeBadge}
            </span>
            <button
              type="button"
              className="text-xs font-semibold text-psg-teal underline bg-transparent border-none cursor-pointer p-0"
              onClick={onOpenPreview}
            >
              Preview
            </button>
          </div>
        </div>

        {isClosed ? (
          <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-800">
            ECDD closed — decision: <strong>{decisionLabel(selectedDecision)}</strong>
            {decisionRationale && (
              <span className="text-green-700"> · {decisionRationale.slice(0, 120)}{decisionRationale.length > 120 ? '…' : ''}</span>
            )}
          </div>
        ) : !formGatesReady && !isPendingApproval && !isApproved ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600 m-0">
              <span className="font-semibold text-amber-700">{formGatesDone}/{formGatesTotal}</span> gates complete —
              finish sections marked <span className="text-red-600 font-semibold">Required</span> in the left menu.
            </p>
            {onScrollToIncomplete && (
              <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={onScrollToIncomplete}>
                Go to first required
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {formGatesReady && !isPendingApproval && !isApproved && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {DECISION_OPTIONS.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => setSelectedDecision(d.key)}
                      className="p-2.5 rounded-lg text-left transition-colors border-2 cursor-pointer"
                      style={{
                        background: selectedDecision === d.key ? `${d.color}10` : '#fafbfc',
                        borderColor: selectedDecision === d.key ? d.color : '#e2e8f0',
                      }}
                    >
                      <div className="text-xs font-bold" style={{ color: d.color }}>{d.label}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{d.desc}</div>
                    </button>
                  ))}
                </div>

                {selectedDecision && (
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Decision rationale</label>
                      <textarea
                        value={decisionRationale}
                        onChange={(e) => setDecisionRationale(e.target.value)}
                        placeholder="Document your reasoning for this decision…"
                        className="w-full min-h-[56px] max-h-28 p-2.5 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white resize-y outline-none focus:border-psg-teal"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0">
                      <Button
                        type="button"
                        className="bg-psg-teal hover:bg-psg-teal-700 whitespace-nowrap"
                        disabled={
                          !selectedDecision ||
                          !decisionRationale.trim() ||
                          submittingDecision
                        }
                        onClick={onSubmitForApproval}
                      >
                        <ShieldCheckIcon className="w-4 h-4 mr-1" />
                        {submittingDecision
                          ? 'Submitting…'
                          : requiresApproval
                            ? 'Submit for approval'
                            : 'Submit & close'}
                      </Button>
                      {submitDecisionError && (
                        <p className="text-xs text-red-600 m-0">{submitDecisionError}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedDecision && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                      <span className="font-semibold uppercase tracking-wide text-slate-500">Approval policies</span>
                      <Link to="/governance-safe/approval-policies" className="font-semibold text-psg-teal hover:underline">
                        Open policies →
                      </Link>
                    </div>
                    {checkingTriggers ? (
                      <span>Checking triggers…</span>
                    ) : matchedTriggers.length > 0 ? (
                      <span>
                        {matchedTriggers.map((t) => t.name).join(', ')}
                        {requiresApproval ? ' — manager approval required.' : ''}
                      </span>
                    ) : (
                      <span>No approval trigger matched — submit may close this ECDD directly.</span>
                    )}
                  </div>
                )}
              </>
            )}

            {isPendingApproval && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Awaiting approval — <strong>{decisionLabel(selectedDecision)}</strong>
                </div>
                {checkingApprovalPermission ? (
                  <span className="text-sm text-slate-500">Checking permissions…</span>
                ) : canApproveEcdd ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={approvingEcdd || returningEcdd}
                      onClick={onApproveEcdd}
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      {approvingEcdd ? 'Approving…' : 'Approve'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-amber-500 text-amber-700"
                      disabled={approvingEcdd || returningEcdd}
                      onClick={() => setShowReturnModal(true)}
                    >
                      <ArrowUturnLeftIcon className="w-4 h-4 mr-1" />
                      Return
                    </Button>
                  </div>
                ) : (
                  <span className="text-sm text-slate-500 italic">No approval permission</span>
                )}
                {approvalError && <p className="text-xs text-red-600 m-0 w-full">{approvalError}</p>}
              </div>
            )}

            {isApproved && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-700">
                  {isApprovedSmr ? 'SMR approved — file SMR then close.' : 'Monitoring approved — add to watchlist then close.'}
                </div>
                <Button
                  type="button"
                  className={isApprovedSmr ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                  disabled={closingEcdd}
                  onClick={onCloseEcdd}
                >
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  {closingEcdd
                    ? 'Closing…'
                    : isApprovedSmr
                      ? 'Mark SMR filed & close'
                      : 'Mark monitoring active & close'}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {showReturnModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Return ECDD for Revision</h3>
            <p className="text-sm text-gray-600 mb-3">
              Please provide a reason for returning this ECDD to the compliance officer.
            </p>
            <textarea
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="Enter reason for return..."
              className="w-full min-h-[100px] p-3 rounded-lg border border-gray-200 text-sm text-gray-700 mb-3"
            />
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowReturnModal(false)
                  setReturnReason('')
                }}
                disabled={returningEcdd}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={!returnReason.trim() || returningEcdd}
                onClick={onReturnEcdd}
              >
                {returningEcdd ? 'Returning…' : 'Return ECDD'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}
